import React, { useEffect, useMemo, useState, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'flowbite-react';
import { HiOutlineAdjustments } from 'react-icons/hi';
import { ReactComponent as Close16Icon } from 'assets/image/svg/close16.svg';
import Button from 'src/components/button/button';
import SearchInput from 'src/components/input/SearchInput';
import SlideUpModal from 'src/components/SlideUpModal';
import Table, { TableColDef } from 'src/components/table/Table';
import TabletActionGroup from 'src/components/table/TableActionGroup';
import DeleteConfirmModal from 'src/components/modal/DeleteConfirmModall';
import { useLoading } from 'src/hooks/useLoading';
import { TouristCenterOverrideType } from 'src/types/touristCenterOverride';
import { useDeleteTouristCenterOverride, useGetTouristCenterOverrides } from 'src/hooks/useTouristCenterOverrides';
import AddEditTouristCenterModal from './AddEditTouristCenterModal';
import { defaultPageCount } from 'src/utils/constants';

type SiteTouristCenterOverridesProps = {
   siteId: string;
};

const SiteTouristCenterOverrides: React.FC<SiteTouristCenterOverridesProps> = () => {
   const titleId = useId();
   const [t] = useTranslation();
   const { setLoading } = useLoading();
   const [searchValue, _setSearchValue] = useState('');
   const [isFetchAllSites, setIsFetchAllSites] = useState(false);
   const [openModal, setOpenModal] = useState(false);
   const [isEdit, setIsEdit] = useState(false);
   const [selectedOverride, setSelectedOverride] = useState<TouristCenterOverrideType | null>(null);
   const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
   const [overrides, setOverrides] = useState<TouristCenterOverrideType[]>([]);
   const [_page, _setPage] = useState(0);
   const [_totalPages, setTotalPages] = useState(0);
   const [totalEntities, setTotalEntities] = useState(0);
   const [openMobileFilterModal, setOpenMobileFilterModal] = useState(false);
   const [filters, setFilters] = useState<any>({
         page: 0,
         limit: defaultPageCount,
   });

   useEffect(() => {
      const handleResize = () => {
         if (window.innerWidth >= 768) setOpenMobileFilterModal(false);
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, []);

   const { data, isFetching, isLoading } = useGetTouristCenterOverrides(filters);

   useEffect(() => {
      if (data?.entities) {
         setOverrides(Array.isArray(data.entities) ? data.entities : []);
         const total = data.totalEntities || 0;
         const totalPages = Math.ceil(total / 10);
         setTotalPages(totalPages);
         setTotalEntities(total)
      }
   }, [data]);

   const { mutateAsync: deleteMutation } = useDeleteTouristCenterOverride({
      onMutateCallBack: () => setLoading(true),
      onSettledCallBack: () => setLoading(false),
   });

   const handleConfirmDelete = async () => {
      if (selectedOverride) {
         await deleteMutation(selectedOverride.id);
         setIsOpenDeleteModal(false);
         setSelectedOverride(null);
      }
   };

   const formatCoordinate = (value: any) => {
      const num = typeof value === 'number' ? value : parseFloat(value);
      return isNaN(num) ? '-' : num.toFixed(4);
   };

   const handleSearch = (value: string) => {
      setFilters((preValues: any) => ({
         ...preValues,
         page: 0,
         searchQuery: encodeURIComponent(value),
      }));
   };

   const columns: TableColDef<TouristCenterOverrideType>[] = useMemo(
      () => [
         {
            headerName: 'Search Location',
            field: 'searchLocation',
            sortable: false,
            minWidth: 200,
            maxWidth: 300,
            flex: 1,
            render: (row: TouristCenterOverrideType) => (
               <p className="self-center mb-0 text-black font-semibold text-sm w-fit line-clamp-2">{row.searchLocation || '-'}</p>
            ),
         },
         {
            headerName: 'Override Name',
            field: 'overrideName',
            sortable: false,
            minWidth: 250,
            maxWidth: 300,
            columnClassName: 'ml-4',
            render: (row: TouristCenterOverrideType) => (
               <p className="self-center mb-0 text-black font-semibold text-sm w-fit line-clamp-2 ml-4">{row.overrideName || '-'}</p>
            ),
         },
         {
            headerName: 'Notes',
            field: 'notes',
            sortable: false,
            minWidth: 400,
            maxWidth: 600,
            flex: 1,
            render: (row: TouristCenterOverrideType) => (
               <p className="self-center mb-0 text-black text-sm w-fit line-clamp-2">{row.notes || '-'}</p>
            ),
         },
         {
            headerName: 'Override Latitude',
            field: 'overrideLatitude',
            sortable: false,
            minWidth: 150,
            maxWidth: 250,
            columnClassName: 'ml-6',
            render: (row: TouristCenterOverrideType) => (
               <p className="self-center mb-0 text-black text-sm w-fit ml-6">{formatCoordinate(row.overrideLatitude)}</p>
            ),
         },
         {
            headerName: 'Override Longitude',
            field: 'overrideLongitude',
            sortable: false,
            minWidth: 150,
            maxWidth: 250,
            render: (row: TouristCenterOverrideType) => (
               <p className="self-center mb-0 text-black text-sm w-fit">{formatCoordinate(row.overrideLongitude)}</p>
            ),
         },
         {
            field: 'actions',
            sortable: false,
            minWidth: 110,
            render: (row: TouristCenterOverrideType) => (
               <div className="flex items-center justify-end w-full">
                  <TabletActionGroup
                     edit
                     onEditClick={() => {
                        setSelectedOverride(row);
                        setIsEdit(true);
                        setOpenModal(true);
                     }}
                     delete
                     onDeleteClick={() => {
                        setSelectedOverride(row);
                        setIsOpenDeleteModal(true);
                     }}
                  />
               </div>
            ),
         },
      ],
      [],
   );

   return (
      <div className="flex flex-col bg-white h-full">
         <div className="flex flex-col md:flex-row gap-4 md:flex-wrap justify-between">
            <div className="flex gap-3 flex-wrap">
               <div className="md:w-[300px] w-full">
                  <SearchInput handleSearch={handleSearch} searchValue={searchValue} preventSubmission />
               </div>
            </div>
            <Button
               className="h-8.5 text-sm w-full md:w-fit shrink-0 md:ml-auto"
               onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsEdit(false);
                  setSelectedOverride(null);
                  setOpenModal(true);
               }}
            >
               {t('Add Override')}
            </Button>
         </div>

         <SlideUpModal isOpen={openMobileFilterModal} setIsOpen={setOpenMobileFilterModal} titleId={titleId}>
            <div className="p-5 flex flex-col gap-4">
               <div className="flex items-center justify-between">
                  <p id={titleId} className="text-base font-semibold">Filter</p>
                  <Close16Icon className="cursor-pointer" onClick={() => setOpenMobileFilterModal(false)} />
               </div>
               <div className="flex flex-col gap-3">
                  <div className="h-8.5 bg-border rounded-6 p-0.5 gap-0.5 flex justify-center items-center">
                     <button
                        disabled={isFetching}
                        onClick={() => setIsFetchAllSites(false)}
                        className={`h-full flex-1 text-sm font-semibold rounded-md px-3.75 text-nowrap ${!isFetchAllSites ? 'bg-primary text-white rounded-6 border' : ''}`}
                     >
                        This Site
                     </button>
                     <button
                        disabled={isFetching}
                        onClick={() => setIsFetchAllSites(true)}
                        className={`h-full flex-1 text-sm font-semibold rounded-md px-3.75 text-nowrap ${isFetchAllSites ? 'bg-primary text-white rounded-6 border' : ''}`}
                     >
                        All Sites
                     </button>
                  </div>
               </div>
            </div>
         </SlideUpModal>

         <div className="flex flex-col flex-1 mt-3 justify-between h-full overflow-hidden">
            <Table ariaLabel={t('admin.tableLabels.touristCenterOverrides')}
                  isLoading={isLoading || isFetching}
                  columns={columns}
                  rows={overrides}
                  pagination={overrides.length > 0 && !isLoading}
                  currentPage={filters.page || 0}
                  onPageChanged={(page: number) => {
                     setFilters((preValues: any) => {
                        return {
                           ...preValues,
                           page: page,
                        };
                     });
                  }}
                  totalPage={Math.ceil(totalEntities / defaultPageCount)}
               />
            <DeleteConfirmModal show={isOpenDeleteModal} onConfirm={handleConfirmDelete} onCancel={() => setIsOpenDeleteModal(false)} />
         </div>

         <AddEditTouristCenterModal
            openModal={openModal || isEdit}
            selectedOverride={selectedOverride}
            onClose={() => {
               setOpenModal(false);
               setSelectedOverride(null);
               setIsEdit(false);
            }}
         />
      </div>
   );
};

export default SiteTouristCenterOverrides;
